import { useEffect, useLayoutEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { VersionBadge } from './VersionBadge';
import { usePermisos } from '../lib/permisos';

/**
 * El MENÚ de DTB HUB (0.39.0): a la izquierda, como Halory y la app de recepción. Cada pantalla sigue
 * poniendo <TopBar /> hasta arriba y esto se encarga de todo:
 *  - pantalla ancha (≥ 900 px): barra lateral fija; el contenido se recorre a la derecha (clase `con-menu` en
 *    el body, ver theme.css), así que ninguna pantalla tuvo que cambiar su maquetado;
 *  - celular: una barra delgada arriba con el botón de menú y el nombre de la sección; el menú sale de la
 *    izquierda encima de todo (fondo oscuro, Escape o tocar fuera lo cierran).
 * Antes era una fila de ligas arriba que en celular se cortaba ("Form…") y el encabezado ocupaba un tercio
 * de la pantalla.
 */
type Ic = 'resumen' | 'alertas' | 'analitica' | 'marketing' | 'comisiones' | 'equipo' | 'app' | 'formularios' | 'admin' | 'reglas';
const PATHS: Record<Ic, string> = {
  resumen: 'M3 11.5 12 4l9 7.5M5 10v10h5v-6h4v6h5V10',
  alertas: 'M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0',
  analitica: 'M4 20V10m6 10V4m6 16v-7m4 7H2',
  marketing: 'M3 11v2a1 1 0 0 0 1 1h2l5 4V6L6 10H4a1 1 0 0 0-1 1Zm13-3a5 5 0 0 1 0 8m3-11a9 9 0 0 1 0 14',
  comisiones: 'M19 5 5 19M7.5 9a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Zm9 9a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z',
  equipo: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm13 10v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75',
  app: 'M7 2h10a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2Zm5 16h.01',
  formularios: 'M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Zm0 0v5h5M9 13h6m-6 4h6',
  admin: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm7.4-3a7.4 7.4 0 0 0-.1-1.2l2-1.6-2-3.4-2.4 1a7 7 0 0 0-2-1.2L14.5 3h-5l-.4 2.6a7 7 0 0 0-2 1.2l-2.4-1-2 3.4 2 1.6a7.4 7.4 0 0 0 0 2.4l-2 1.6 2 3.4 2.4-1a7 7 0 0 0 2 1.2l.4 2.6h5l.4-2.6a7 7 0 0 0 2-1.2l2.4 1 2-3.4-2-1.6c.1-.4.1-.8.1-1.2Z',
  reglas: 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10ZM8 8h8m-8 4h5',
};
function Icono({ n }: { n: Ic }) {
  return <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={PATHS[n]} /></svg>;
}

interface Item { to: string; label: string; ic: Ic }

export function TopBar() {
  const { user, logout } = useAuth();
  const { puede, esAdmin } = usePermisos();
  const [abierto, setAbierto] = useState(false);
  const loc = useLocation();

  // La barra lateral ocupa su lugar: el body se recorre (ver .con-menu en theme.css). Al salir de la pantalla
  // (Login no tiene menú) se quita.
  useLayoutEffect(() => {
    document.body.classList.add('con-menu');
    return () => document.body.classList.remove('con-menu');
  }, []);
  // El menú del celular se cierra al cambiar de pantalla y con Escape; mientras está abierto la página de
  // atrás no se desliza.
  useEffect(() => { setAbierto(false); }, [loc.pathname, loc.search]);
  useEffect(() => {
    if (!abierto) return;
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setAbierto(false); };
    window.addEventListener('keydown', esc);
    document.body.classList.add('menu-abierto');
    return () => { window.removeEventListener('keydown', esc); document.body.classList.remove('menu-abierto'); };
  }, [abierto]);

  if (!user) return null;
  // El menú enseña SÓLO lo que esta cuenta abre (Administración → Permisos, lib/permisos.ts): la misma regla que las
  // rutas (ProtectedRoute) y que el API. Lo de siempre: una cuenta de admin todo; el equipo, el Resumen y "Mis
  // comisiones"; marketing, Marketing (sólo la galería) y App de pacientes.
  const it = (s: Parameters<typeof puede>[0], item: Item): Item[] => (puede(s) ? [item] : []);
  const grupos: { titulo?: string; items: Item[] }[] = [
    // Alertas (0.40.0) reemplaza a "Mi día": quejas, chats sin respuesta y citas caídas, para dirección.
    { items: [...it('resumen', { to: '/', label: 'Resumen', ic: 'resumen' }), ...it('alertas', { to: '/alertas', label: 'Alertas', ic: 'alertas' })] },
    {
      titulo: 'Números',
      items: [
        ...it('analitica', { to: '/analitica', label: 'Analítica', ic: 'analitica' }),
        ...it(['marketing', 'galeria'], { to: '/marketing', label: 'Marketing', ic: 'marketing' }),
        // Una cuenta de admin ve las de todo el equipo; las demás, sólo las suyas (el servidor filtra por la sesión).
        ...it('comisiones', { to: '/comisiones', label: esAdmin ? 'Comisiones' : 'Mis comisiones', ic: 'comisiones' }),
        ...it('equipo', { to: '/equipo', label: 'Equipo', ic: 'equipo' }),
      ],
    },
    { titulo: 'Pacientes', items: [...it('app_pacientes', { to: '/app-pacientes', label: 'App de pacientes', ic: 'app' }), ...it('formularios', { to: '/formularios', label: 'Formularios web', ic: 'formularios' })] },
    { titulo: 'Ajustes', items: [...it('admin', { to: '/admin', label: 'Administración', ic: 'admin' }), ...it('reglas', { to: '/config', label: 'Reglas y datos', ic: 'reglas' })] },
  ];
  // Con pocas secciones (el equipo: Resumen y "Mis comisiones") los títulos de grupo sobran.
  const conTitulos = grupos.reduce((n, g) => n + g.items.length, 0) > 3;
  const todos = grupos.flatMap((g) => g.items);
  // El nombre de la sección para la barra del celular.
  const actual = todos.find((i) => (i.to === '/' ? loc.pathname === '/' : loc.pathname.startsWith(i.to)))
    || (loc.pathname.startsWith('/paciente/') ? { label: 'Ficha' } : loc.pathname.startsWith('/mi-cuenta') ? { label: 'Mi cuenta' } : null);

  const menu = (
    <>
      <div className="hub-marca">
        <img src="/icons/DTB_1-192.png" width={34} height={34} alt="" />
        <strong>DTB HUB</strong>
      </div>
      <nav className="hub-nav" aria-label="Secciones">
        {grupos.filter((g) => g.items.length).map((g, i) => (
          <div key={i} className="hub-grupo">
            {g.titulo && conTitulos && <div className="hub-grupo-t">{g.titulo}</div>}
            {g.items.map((it) => (
              <NavLink key={it.to + it.label} to={it.to} end={it.to === '/'} className={({ isActive }) => (isActive ? 'activo' : '')} data-nav={it.to}>
                <Icono n={it.ic} /><span>{it.label}</span>
              </NavLink>
            ))}
          </div>
        ))}
      </nav>
      <div className="hub-pie">
        {/* "Mi cuenta" (0.47.0): el PIN para confirmar lo delicado. Quién eres se toca y lleva ahí. */}
        <NavLink to="/mi-cuenta" className={({ isActive }) => `hub-quien${isActive ? ' activo' : ''}`} data-nav="/mi-cuenta" title="Mi cuenta: tu PIN">
          <b>{user.name}</b><span>{user.tenantName}</span><span className="hub-mi-cuenta">Mi cuenta</span>
        </NavLink>
        <div className="hub-pie-fila"><VersionBadge /><button type="button" className="btn-ghost" onClick={logout} data-salir>Salir</button></div>
      </div>
    </>
  );

  return (
    <>
      {/* Celular: barra delgada con el botón de menú */}
      <header className="hub-barra">
        <button type="button" className="hub-hamb" aria-label="Abrir el menú" aria-expanded={abierto} aria-controls="hub-menu" onClick={() => setAbierto(true)} data-abrir-menu>
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M3 6h18M3 12h18M3 18h18" /></svg>
        </button>
        <img src="/icons/DTB_1-192.png" width={28} height={28} alt="" />
        <strong className="hub-barra-t">{actual ? actual.label : 'DTB HUB'}</strong>
      </header>
      {abierto && <div className="hub-fondo" onClick={() => setAbierto(false)} aria-hidden="true" />}
      <aside id="hub-menu" className={`hub-menu ${abierto ? 'abierto' : ''}`} aria-label="Menú">
        <button type="button" className="hub-cerrar" aria-label="Cerrar el menú" onClick={() => setAbierto(false)}>
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" /></svg>
        </button>
        {menu}
      </aside>
    </>
  );
}
